import { useMemo, useState } from 'react'
import { Flame, Footprints, Timer, Trash2, Trophy } from 'lucide-react'
import { BarChart } from '../components/BarChart.js'
import {
  Bar,
  Card,
  Figure,
  Screen,
  ScreenBody,
  SectionTitle,
  Segmented,
  StatTile,
} from '../components/Ui.js'
import { ICON_STROKE } from '../lib/icons.js'
import { photo } from '../lib/photos.js'
import { summaryBuckets, weekBuckets, type Period } from '../lib/progress.js'
import { useGame } from '../lib/useGame.js'
import { useT } from '../lib/useT.js'
import { useStore } from '../store/useStore.js'

/** Nombre de semaines affichées par période. */
const WEEKS: Record<Exclude<Period, 'week'>, number> = { month: 5, quarter: 12 }

export function History() {
  const t = useT()
  const game = useGame()
  const activities = useStore((s) => s.activities)
  const removeActivity = useStore((s) => s.removeActivity)

  const [period, setPeriod] = useState<Period>('week')
  const [picked, setPicked] = useState<string | null>(null)

  const buckets = useMemo(
    () =>
      period === 'week'
        ? weekBuckets(activities, game.now)
        : summaryBuckets(game.summaries, WEEKS[period], (date) =>
            t('history.weekOf', { date }),
          ),
    [period, activities, game.now, game.summaries, t],
  )

  // Par défaut, la barre de la période en cours est sélectionnée.
  const selected =
    buckets.find((bucket) => bucket.key === picked) ??
    buckets.find((bucket) => bucket.isCurrent) ??
    buckets[buckets.length - 1]!

  const totals = buckets.reduce(
    (sum, bucket) => ({
      minutes: sum.minutes + bucket.minutes,
      sessions: sum.sessions + bucket.sessions,
      kcal: sum.kcal + bucket.kcal,
    }),
    { minutes: 0, sessions: 0, kcal: 0 },
  )

  // La moyenne ne compte que ce qui est écoulé : les jours à venir ne la
  // tirent pas vers le bas.
  const elapsed = Math.max(1, buckets.filter((bucket) => !bucket.isFuture).length)

  const dateFormat = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })

  return (
    <Screen>
      <ScreenBody className="safe-top pb-8">
        <h1 className="text-display mb-6 font-bold">{t('history.title')}</h1>

        <Segmented
          label={t('history.period')}
          value={period}
          onChange={(value) => {
            setPeriod(value)
            setPicked(null)
          }}
          options={[
            { value: 'week', label: t('history.periodWeek') },
            { value: 'month', label: t('history.periodMonth') },
            { value: 'quarter', label: t('history.periodQuarter') },
          ]}
        />

        <Card className="mt-4">
          <div className="mb-2 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-caption text-ink-3">{t('history.chartTitle')}</p>
              <Figure value={totals.minutes} unit="min" size="display" />
            </div>
            <span className="bg-sunken/70 text-caption text-ink-2 mt-1 truncate rounded-full px-3 py-1.5 font-semibold first-letter:uppercase">
              {selected.fullLabel}
            </span>
          </div>
          <BarChart buckets={buckets} selected={selected.key} onSelect={setPicked} />
        </Card>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <StatTile
            icon={Footprints}
            label={t('history.sessions')}
            value={totals.sessions}
          />
          <StatTile
            icon={Flame}
            tone="accent"
            label={t('history.kcal')}
            value={totals.kcal}
            unit="kcal"
          />
          <StatTile
            icon={Timer}
            tone="lilac"
            label={t(period === 'week' ? 'history.avgDay' : 'history.avgWeek')}
            value={Math.round(totals.minutes / elapsed)}
            unit="min"
          />
          <StatTile
            icon={Trophy}
            label={t('history.streak')}
            value={game.streak.current}
            unit={t('history.streakUnit')}
          />
        </div>

        <section className="mt-8">
          <SectionTitle>{t('history.thisWeek')}</SectionTitle>
          {/* Les deux "lignes d'énergie" du cahier des charges. Elles ne se
              distinguent jamais par la seule couleur : chacune a son libellé
              et son chiffre. */}
          <Card className="space-y-5">
            <Gauge
              label={t('week.goalLabel')}
              progress={game.goalProgress}
              done={game.week.totalMinutes}
              goal={game.weeklyGoalMin}
            />
            <Gauge
              label={t('week.whoLabel')}
              progress={game.whoProgress}
              done={game.week.whoMinutes}
              goal={150}
            />
          </Card>
        </section>

        <section className="mt-8">
          <SectionTitle>{t('history.journal')}</SectionTitle>
          {activities.length === 0 ? (
            <Card tone="sunken" className="py-10 text-center">
              <p className="text-body font-semibold">{t('activity.empty')}</p>
              <p className="text-caption text-ink-3 mt-1">{t('activity.emptyHint')}</p>
            </Card>
          ) : (
            <ul className="space-y-2">
              {activities.map((activity) => {
                return (
                  <li key={activity.id} className="card flex items-center gap-3 py-2 pr-2 pl-2">
                    <img
                      src={photo(activity.type)}
                      alt=""
                      loading="lazy"
                      className="size-14 shrink-0 rounded-[var(--radius-tile)] object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-body truncate font-bold">
                        {t(`activityType.${activity.type}`)}
                      </p>
                      <p className="text-caption text-ink-3 truncate">
                        {dateFormat.format(new Date(activity.startedAt))}
                      </p>
                    </div>
                    <span className="text-body num font-bold">
                      {activity.durationMin}
                      <span className="text-caption ml-0.5">min</span>
                    </span>
                    <button
                      onClick={() => removeActivity(activity.id)}
                      className="text-ink-3 grid size-11 shrink-0 place-items-center rounded-full"
                      aria-label={t('common.delete')}
                    >
                      <Trash2 size={17} strokeWidth={ICON_STROKE} aria-hidden="true" />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </ScreenBody>
    </Screen>
  )
}

function Gauge({
  label,
  progress,
  done,
  goal,
}: {
  label: string
  progress: number
  done: number
  goal: number
}) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-body font-semibold">{label}</span>
        <span className="text-caption num shrink-0">
          <span className="text-ink font-bold">{Math.round(done)}</span>
          <span className="text-ink-3"> / {goal} min</span>
        </span>
      </div>
      <Bar progress={progress} tone={progress >= 1 ? 'green' : 'accent'} label={label} />
    </div>
  )
}
